// useEffectiveRole — the "what UI should this user see" hook. Combines
// two inputs:
//
//   1. Real auth role from /api/me (editor / pmm / viewer / sales / none).
//   2. The user's chosen Persona (EPD / Enablement / Sales) from the
//      top-of-nav selector.
//
// Persona *caps* the effective role — it can only demote, never promote.
// A real editor picking Sales sees the sales UI (no drafts, no editor
// tools, no forward dates); a real sales user picking EPD stays capped
// at sales. The persona is a lens, not a privilege grant.
//
// Server-side auth is still authoritative — this is client-side chrome
// only. Anyone determined to inspect an editor-only response can still
// see it in DevTools. The point is that the app *behaves* as the chosen
// role, not that it's a security boundary.
//
// Also clears the legacy `release_hub_view_mode` localStorage entry on
// module load so lingering "sales"/"viewer" values from earlier
// sessions don't confuse anything that might still read them.
import type { HubUser, HubUserRole, Persona } from "@release-hub/shared";
import { usePersona } from "./Persona";
import { useUser } from "./useUser";

const LEGACY_STORAGE_KEY = "release_hub_view_mode";
if (typeof window !== "undefined") {
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    /* private-mode etc — ignore */
  }
}

export interface EffectiveRole {
  isEditor: boolean;
  isPmm: boolean;
  isViewer: boolean;
  isSales: boolean;
  /** Editor OR PMM — can save customer-facing copy. */
  canEditContent: boolean;
  user: HubUser | null;
}

/** Access rank — higher grants strictly more chrome than lower. Persona
 *  computes a "max role" and we take the min of that and the real role. */
const ROLE_RANK: Record<HubUserRole, number> = {
  editor: 4,
  pmm: 3,
  viewer: 2,
  sales: 1,
  none: 0,
};

/** Ceiling that a given persona lets a user express, before real-auth
 *  capping. EPD keeps whatever real auth grants; Enablement caps at
 *  PMM (editable customer copy, no core-entity edits); Sales caps at
 *  the sales tier (read-only + forward-date scrub). */
const PERSONA_CEILING: Record<Persona, HubUserRole> = {
  epd: "editor",
  enablement: "pmm",
  sales: "sales",
};

function effectiveFrom(real: HubUserRole, persona: Persona): HubUserRole {
  const ceiling = PERSONA_CEILING[persona];
  return ROLE_RANK[ceiling] < ROLE_RANK[real] ? ceiling : real;
}

/** Effective role — real auth capped by the chosen persona. Pages should
 *  read from this everywhere they gate UI; the top-of-nav persona
 *  selector is the single knob users turn. */
export function useEffectiveRole(): EffectiveRole {
  const { user, role: realRole } = useUser();
  const { persona } = usePersona();
  const eff = effectiveFrom(realRole, persona);
  const isEditor = eff === "editor";
  const isPmm = eff === "pmm";
  return {
    isEditor,
    isPmm,
    isViewer: eff === "viewer",
    isSales: eff === "sales",
    canEditContent: isEditor || isPmm,
    user,
  };
}
